<script setup lang="ts">
import {
  Layers,
  TreeDeciduous,
  Mountain,
  Droplets,
  Building,
  MapPin,
  Navigation,
  Bike,
  ChevronDown,
  ChevronRight,
  Home,
  Train,
  Plane,
  Cable,
  Globe,
  MapPin as POI,
  Tag,
} from '@lucide/vue'
import { storeToRefs } from 'pinia'

import BaseCheckbox from '@/components/ui/BaseCheckbox.vue'
import { useMapStore } from '@/stores/mapStore'
import { useUiStore } from '@/stores/uiStore'

/**
 * Presentational list of map-layer categories with per-category and per-layer toggles. Self-contained
 * (reads/writes the map + ui stores directly), so the desktop floating panel (LayersPanel) and the
 * mobile bottom sheet (MapLayersSheet) both render this without prop plumbing.
 */
defineOptions({ name: 'LayersList' })

const mapStore = useMapStore()
const uiStore = useUiStore()
const { layerVisibility } = storeToRefs(mapStore)
const { layerPanelExpandedCategories } = storeToRefs(uiStore)

const categories = [
  { id: 'base', name: 'Base Map', icon: Layers },
  { id: 'pointsOfInterest', name: 'Points of Interest', icon: POI },
  { id: 'terrain', name: 'Relief', icon: Mountain },
  { id: 'vegetation', name: 'Vegetation', icon: TreeDeciduous },
  { id: 'water', name: 'Water', icon: Droplets },
  { id: 'urban', name: 'Urban Areas', icon: Home },
  { id: 'buildings', name: 'Buildings', icon: Building },
  { id: 'roads', name: 'Roads', icon: MapPin },
  { id: 'railways', name: 'Railways', icon: Train },
  { id: 'aviation', name: 'Aviation', icon: Plane },
  { id: 'trails', name: 'Trails', icon: Navigation },
  { id: 'cycling', name: 'Cycling', icon: Bike },
  { id: 'lifts', name: 'Cable Cars & Lifts', icon: Cable },
  { id: 'boundaries', name: 'Borders', icon: Globe },
  { id: 'labels', name: 'Map Labels', icon: Tag },
]

const poiLayerLabels: Record<string, string> = {
  pois_beach: 'Beach',
  pois_forest: 'Forest',
  pois_marina: 'Marina',
  pois_park: 'Park',
  pois_zoo: 'Zoo',
  pois_garden: 'Garden',
  pois_aerodrome: 'Aerodrome',
  pois_station: 'Station',
  pois_bus_stop: 'Bus stop',
  pois_ferry_terminal: 'Ferry terminal',
  pois_stadium: 'Stadium',
  pois_university: 'University',
  pois_library: 'Library',
  pois_school: 'School',
  pois_animal: 'Animal',
  pois_post_office: 'Post office',
  pois_building: 'Building',
  pois_townhall: 'Town hall',
  pois_restaurant: 'Restaurant',
  pois_fast_food: 'Fast food',
  pois_cafe: 'Cafe',
  pois_bar: 'Bar',
  pois_supermarket: 'Supermarket',
  pois_convenience: 'Convenience store',
  pois_books: 'Books',
  pois_beauty: 'Beauty',
  pois_electronics: 'Electronics',
  pois_clothes: 'Clothes',
  pois_attraction: 'Attraction',
  pois_museum: 'Museum',
  pois_theatre: 'Theatre',
  pois_artwork: 'Artwork',
  pois_peak: 'Peak',
  pois_viewpoint: 'Viewpoint',
  pois_shelter: 'Shelter',
  pois_wilderness_hut: 'Wilderness hut',
  pois_alpine_hut: 'Alpine hut',
  pois_camp_site: 'Campsite',
  pois_picnic_site: 'Picnic site',
  pois_picnic_table: 'Picnic table',
  pois_drinking_water: 'Drinking water',
  pois_water_point: 'Water point',
  pois_watering_place: 'Watering place',
  pois_fountain: 'Fountain',
  pois_spring: 'Spring',
  pois_toilets: 'Toilets',
  pois_bench: 'Bench',
  pois_information: 'Information',
  pois_trailhead: 'Trailhead',
  pois_parking: 'Parking',
  pois_bicycle_parking: 'Bicycle parking',
  pois_bicycle_repair_station: 'Bicycle repair station',
}

const layerLabels: Record<string, string> = {
  roads_cycleway: 'Cycleways',
  roads_aerialway_cable_car: 'Cable cars',
}

const toggleExpand = (categoryId: string) => {
  const current = layerPanelExpandedCategories.value[categoryId] ?? false
  uiStore.setLayerPanelCategoryExpanded(categoryId, !current)
}

const toggleLayer = (layerId: string, visible: boolean) => {
  mapStore.toggleLayer(layerId, visible)
  uiStore.setLayerVisibility(layerId, visible)
}

const toggleCategory = (categoryId: string, visible: boolean) => {
  mapStore.toggleCategory(categoryId, visible)
  getCategoryLayers(categoryId).forEach((layerId) => {
    uiStore.setLayerVisibility(layerId, visible)
  })
}

const getCategoryLayers = (categoryId: string) => {
  return mapStore.getCategoryLayers(categoryId).filter((id) => mapStore.layerExists(id))
}

const isCategoryVisible = (categoryId: string) => mapStore.isCategoryVisible(categoryId)
const isCategoryPartiallyVisible = (categoryId: string) =>
  mapStore.isCategoryPartiallyVisible(categoryId)

const formatLayerId = (layerId: string): string =>
  layerLabels[layerId] ?? poiLayerLabels[layerId] ?? layerId.replace(/[-_]/g, ' ')
</script>

<template>
  <ul class="space-y-1">
    <li
      v-for="category in categories"
      :key="category.id"
      class="border-t border-gray-100 pt-2 first:border-t-0 first:pt-0"
    >
      <!-- Category header with expand/collapse icon -->
      <div
        class="flex min-h-11 cursor-pointer items-center justify-between rounded px-2 py-1 hover:bg-gray-50"
        @click="toggleExpand(category.id)"
      >
        <div class="flex items-center">
          <component :is="category.icon" class="mr-2 h-4 w-4 text-gray-600" />
          <span class="text-sm font-medium">{{ category.name }}</span>
        </div>
        <div class="flex items-center space-x-2">
          <span class="text-xs text-gray-500"
            >{{ getCategoryLayers(category.id).length }} layers</span
          >
          <!-- Toggle checkbox: fully checked / indeterminate (partial) / unchecked -->
          <BaseCheckbox
            :model-value="isCategoryVisible(category.id)"
            :indeterminate="isCategoryPartiallyVisible(category.id)"
            :aria-label="`Toggle ${category.name} visibility`"
            @click.stop
            @update:model-value="(checked) => toggleCategory(category.id, checked)"
          />
          <component
            :is="layerPanelExpandedCategories[category.id] ? ChevronDown : ChevronRight"
            class="h-4 w-4 text-gray-500"
            :class="{ 'text-gray-700': layerPanelExpandedCategories[category.id] }"
            aria-hidden="true"
          />
        </div>
      </div>

      <!-- Category layers (collapsible) -->
      <div v-if="layerPanelExpandedCategories[category.id]" class="mt-1 space-y-1 pl-6">
        <div
          v-for="layerId in getCategoryLayers(category.id)"
          :key="layerId"
          class="flex items-center justify-between py-1"
        >
          <span class="max-w-[90%] truncate text-xs" :title="layerId">
            {{ formatLayerId(layerId) }}
          </span>
          <BaseCheckbox
            size="sm"
            :model-value="layerVisibility[layerId]"
            :aria-label="`Toggle ${formatLayerId(layerId)} visibility`"
            @update:model-value="(checked) => toggleLayer(layerId, checked)"
          />
        </div>
        <div
          v-if="getCategoryLayers(category.id).length === 0"
          class="py-1 text-xs italic text-gray-400"
        >
          No visible layers
        </div>
      </div>
    </li>
  </ul>
</template>
